import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { connect } from 'react-redux';
import { AxiosResponse } from 'axios';
import FormikErrorFocus from 'formik-error-focus';

import { AuthTitle, AuthSubtitle } from '../index.styles';
import AuthInput from '../../../../features/auth/components/authInput';
import AuthButton from '../../../../features/auth/components/authSubmitButton';
import { updatePasswordAction } from '../../store/actions';
import { verifyToken } from '../../../../features/auth/store/api';
import { UpdatePasswordFromBody, getMobileOperatingSystem } from '../../../../features/auth/utilites';
import { Stack, Typography } from '@mui/material';
import ExternalLink from '../../../../shared/components/icons/externalLink';
import RouteLink from '../../../../shared/components/routeLink';

const updatePasswordValidation = () => {
  const updatePasswordSchema = {
    password: Yup
      .string()
      .strict()
      .trim('Password should not include trailing whitespaces')
      .required('This field is required')
      .matches(
        new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z]).{8,}$"),
        'Password should be at least 8 chars long, must contains at least 1 uppercase letter, 1 lowercase letter and 1 number or 1 symbol'
      )
      .min(
        8,
        'Password should be minimum 8 chars atleast'
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords do not match')
      .required('This field is required')
  };
  return Yup.object().shape(updatePasswordSchema);
};

function UpdatePassword(updatePasswordProps: {
  // eslint-disable-next-line no-unused-vars
  updatePassword: (data: UpdatePasswordFromBody) => void;
}) {
  const [tokenApiCalled, setTokenApiCalled] = useState('');
  const [verifiedData, setVerifiedData] = useState<{
    verified?: boolean; email?: string;
  }>();
  const { token } = useParams<{ token: string; }>();

  const initialValues = {
    password: '',
    confirmPassword: '',
  };
  const { updatePassword } = updatePasswordProps;

  const handleSubmit = (data: typeof initialValues) => {
    updatePassword({
      token: token,
      password: data.password
    });
  };

  useEffect(() => {
    if (token && !tokenApiCalled) {
      verifyToken(token)
        .then((res: AxiosResponse) => {
          setVerifiedData(res.data.data);
          setTokenApiCalled('SUCCESS');
          const os = getMobileOperatingSystem();
          if (os === 'Android' || os === 'iOS') {
            window.location.assign(`https://claraforcreators.page.link/?link=https://claraforcreators.com/auth/login?token=${token}&apn=com.clara.claraforcreators&isi=1594528084&ibi=com.clara.claraforcreators`);
          }
        })
        .catch(() => {
          setTokenApiCalled('ERROR');
        });
    }
  }, [token]);

  return (
    <Box sx={{ height: '90%' }}>
      {tokenApiCalled === 'SUCCESS' && (
        <>{verifiedData.verified && (
          <>
            <AuthTitle variant='h4' fontWeight="bold">
              Update Password
            </AuthTitle>
            <AuthSubtitle
              variant='body1'
            >
              Enter new password for <span className='em'>{verifiedData.email}</span>
            </AuthSubtitle>
          </>
        )}</>)
      }
      {tokenApiCalled === 'SUCCESS' && (
        <>{verifiedData.verified ? (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={updatePasswordValidation()}
            enableReinitialize
            validateOnBlur
          >
            <Form>
              <AuthInput
                label='New Password'
                name='password'
                type='password'
                autoFocus
              />
              <AuthInput
                label='Repeat Password'
                name='confirmPassword'
                type='password'
              />
              <AuthButton
                variant='contained'
                type='submit'
              >
                Update Password
              </AuthButton>
              <FormikErrorFocus
                offset={0}
                align="middle"
                focusDelay={10}
                ease="linear"
                duration={500}
              />
            </Form>
          </Formik>
        ) : (
          <Stack sx={{ height: '100%' }} justifyContent='center' alignItems='center'>

            <ExternalLink />
            <Typography
              variant='subtitle2'
              sx={{ mt: 1, mb: 1, color: 'claraPalette.gray1' }}
            >
              This link is expired...&nbsp;
              <RouteLink
                to='/auth'
              >
                Go to Sign in
              </RouteLink>
            </Typography>
          </Stack>
        )}</>
      )}

    </Box>
  );
}

export default connect(null, {
  updatePassword: updatePasswordAction
})(UpdatePassword);